import React, { useMemo, useState } from "react";
import SlidePanel from "../../shared/SlidePanel";
import "../../shared/ui.css";
import "../../new-service/ServiceDetailPanel.css";
import {
  PAY_BASES,
  buildBreakdown,
  computePay,
  groupBreakdown,
  explainPay,
  formatHours,
  formatMoney,
  rateSuffix,
} from "./payCalc";
import "./StaffPayDetail.css";

function initials(name = "") {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
}

const dayFmt = { weekday: "short", day: "numeric", month: "short" };
const weekFmt = { day: "numeric", month: "short" };

/**
 * StaffPayDetail — read-only pay summary for one employee in a SlidePanel,
 * with an explicit Edit affordance (view-before-edit). Editing previews the
 * resulting pay live. The hours breakdown adapts: a daily list for short
 * ranges, weekly rollups (expandable) for long ones.
 */
function StaffPayDetail({ staff, config, currency = "£", start, end, rangeLabel, onClose, onSave }) {
  const cfg = config; // { basis, rate } | null
  const [editing, setEditing] = useState(!cfg); // straight to edit when no pay set yet
  const [basis, setBasis] = useState(cfg?.basis || "Hourly");
  const [rate, setRate] = useState(cfg?.rate != null ? String(cfg.rate) : "");
  const [openWeek, setOpenWeek] = useState(null);

  const days = useMemo(() => buildBreakdown(staff.rotaInfo, start, end), [staff, start, end]);

  // Effective basis/rate: live edit values while editing, saved config otherwise.
  const effBasis = editing ? basis : cfg?.basis || "Hourly";
  const effRate = editing ? parseFloat(rate) || 0 : cfg?.rate || 0;
  const hasRate = editing ? rate !== "" && parseFloat(rate) > 0 : !!cfg;

  const calc = useMemo(
    () => computePay({ payBasis: effBasis, payRate: effRate, days, rotaInfo: staff.rotaInfo }),
    [effBasis, effRate, days, staff]
  );
  const grouped = useMemo(() => groupBreakdown(days), [days]);
  const explain = explainPay(effBasis, calc);
  const isSalaried = effBasis === "Weekly" || effBasis === "Monthly";

  const save = () => {
    onSave(staff, { basis, rate: parseFloat(rate) || 0 });
    setEditing(false);
  };
  const cancel = () => {
    if (cfg) {
      setBasis(cfg.basis);
      setRate(cfg.rate != null ? String(cfg.rate) : "");
      setEditing(false);
    } else {
      onClose();
    }
  };

  return (
    <SlidePanel onClose={onClose} className="sdp-panel spd-panel" width={460}>
      {/* Header */}
      <div className="sdp-header">
        <div className="spd-head-id">
          <div className="spd-avatar">{initials(staff.name)}</div>
          <div className="sdp-header-info">
            <h2 className="sdp-header-name">{staff.name}</h2>
            <span className="sdp-header-category">Employed</span>
          </div>
        </div>
        <button className="beu-icon-btn spd-close" onClick={onClose}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <div className="sdp-body">
        {/* Pay headline */}
        <div className="spd-headline">
          <span className="spd-headline-label">{editing ? "Estimated pay" : "Pay"} · {rangeLabel}</span>
          <span className="spd-headline-value">
            {hasRate && !calc.needsRota ? formatMoney(calc.pay, currency) : "Not set"}
          </span>
          {hasRate && !calc.needsRota && explain.factorValue && (
            <span className="spd-headline-calc">
              {explain.factorValue} {explain.sep} {formatMoney(effRate, currency)} {explain.rateLabel}
              {isSalaried && calc.daysWorked > 0 && (
                <span className="spd-headline-daily"> · ≈ {formatMoney(calc.dailyRate, currency)} / scheduled day</span>
              )}
            </span>
          )}
        </div>

        {/* No-rota guard for salaried staff */}
        {hasRate && calc.needsRota && (
          <div className="spd-norota">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" />
            </svg>
            <span>
              {staff.name.split(" ")[0]} has no rota set, so a {effBasis.toLowerCase()} salary can't be split across working days. Add their weekly schedule in <strong>Schedules &amp; Hours</strong> to calculate pay.
            </span>
          </div>
        )}

        {/* Pay configuration: view or edit */}
        {editing ? (
          <div className="spd-edit">
            <p className="beu-section-label">Pay basis</p>
            <div className="spd-segment">
              {PAY_BASES.map((b) => (
                <button
                  key={b}
                  className={`spd-seg-btn${basis === b ? " active" : ""}`}
                  onClick={() => setBasis(b)}
                  type="button"
                >
                  {b}
                </button>
              ))}
            </div>

            <p className="beu-section-label" style={{ marginTop: 18 }}>Rate</p>
            <div className="spd-rate-field">
              <span className="spd-rate-symbol">{currency}</span>
              <input
                type="number"
                inputMode="decimal"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                placeholder="0.00"
                autoFocus
              />
              <span className="spd-rate-suffix">{rateSuffix(basis)}</span>
            </div>
            <p className="spd-edit-hint">
              {basis === "Hourly"
                ? "Pay is hours worked × this rate."
                : basis === "Daily"
                ? "Pay is days worked × this rate."
                : basis === "Weekly"
                ? "A weekly salary, split across the days in the period."
                : "A monthly salary, prorated across the days in the period."}
            </p>
          </div>
        ) : (
          <button className="spd-config-card" onClick={() => setEditing(true)}>
            <div className="spd-config-main">
              <span className="spd-config-label">{cfg.basis} rate</span>
              <span className="spd-config-rate">
                {formatMoney(cfg.rate, currency)} <span className="spd-config-suffix">{rateSuffix(cfg.basis)}</span>
              </span>
            </div>
            <span className="spd-config-edit">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Edit
            </span>
          </button>
        )}

        {/* Hours stats */}
        <div className="sdp-stats-row spd-stats">
          <div className="sdp-stat">
            <span className="sdp-stat-value">{formatHours(calc.totalHours)}</span>
            <span className="sdp-stat-label">Hours</span>
          </div>
          <div className="sdp-stat-divider" />
          <div className="sdp-stat">
            <span className="sdp-stat-value">{calc.daysWorked}</span>
            <span className="sdp-stat-label">Days</span>
          </div>
          {calc.leaveDays > 0 && (
            <>
              <div className="sdp-stat-divider" />
              <div className="sdp-stat">
                <span className="sdp-stat-value">{calc.leaveDays}</span>
                <span className="sdp-stat-label">Leave</span>
              </div>
            </>
          )}
        </div>

        {/* Overrun */}
        {calc.overrunDays > 0 && (
          <div className="spd-overrun">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <span>
              Worked {formatHours(calc.overrunMins / 60)} past shift across {calc.overrunDays} day
              {calc.overrunDays === 1 ? "" : "s"}. Extend the shift on the calendar to include it in pay.
            </span>
          </div>
        )}

        {/* Breakdown */}
        <div className="spd-section">
          <div className="spd-section-head">
            <p className="beu-section-label" style={{ margin: 0 }}>Hours breakdown</p>
            {grouped.mode === "week" && <span className="spd-section-note">By week</span>}
          </div>

          {grouped.rows.length === 0 ? (
            <div className="spd-empty-break">No scheduled hours in this period.</div>
          ) : grouped.mode === "day" ? (
            <div className="spd-break">
              {grouped.rows.map((d) => (
                <div className="spd-day" key={d.key}>
                  <span className="spd-day-date">{d.date.toLocaleDateString("en-GB", dayFmt)}</span>
                  {d.isLeave ? (
                    <span className="spd-day-leave">{d.leaveType || "Leave"} · {formatHours(d.minutes / 60)}</span>
                  ) : (
                    <span className="spd-day-hours">{formatHours(d.minutes / 60)}</span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="spd-break">
              {grouped.rows.map((w) => {
                const isOpen = openWeek === w.key;
                const pct = grouped.maxMinutes ? Math.round((w.minutes / grouped.maxMinutes) * 100) : 0;
                return (
                  <div className={`spd-week${isOpen ? " open" : ""}`} key={w.key}>
                    <button className="spd-week-head" onClick={() => setOpenWeek(isOpen ? null : w.key)}>
                      <div className="spd-week-info">
                        <span className="spd-week-range">
                          {w.weekStart.toLocaleDateString("en-GB", weekFmt)} to {w.weekEnd.toLocaleDateString("en-GB", weekFmt)}
                        </span>
                        <span className="spd-week-meta">
                          {w.daysWorked} day{w.daysWorked === 1 ? "" : "s"}
                          {w.leaveDays > 0 ? ` · ${w.leaveDays} leave` : ""}
                        </span>
                      </div>
                      <div className="spd-week-bar">
                        <span className="spd-week-bar-fill" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="spd-week-hours">{formatHours(w.minutes / 60)}</span>
                      <svg className="spd-week-chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </button>
                    {isOpen && (
                      <div className="spd-week-days">
                        {w.days.map((d) => (
                          <div className="spd-day spd-day--nested" key={d.key}>
                            <span className="spd-day-date">{d.date.toLocaleDateString("en-GB", dayFmt)}</span>
                            {d.isLeave ? (
                              <span className="spd-day-leave">{d.leaveType || "Leave"} · {formatHours(d.minutes / 60)}</span>
                            ) : (
                              <span className="spd-day-hours">{formatHours(d.minutes / 60)}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="sdp-footer">
        {editing ? (
          <>
            <button className="sdp-btn sdp-btn--neutral" onClick={cancel}>Cancel</button>
            <button className="sdp-btn sdp-btn--primary" onClick={save}>{cfg ? "Save pay" : "Set pay"}</button>
          </>
        ) : (
          <>
            <button className="sdp-btn sdp-btn--neutral" onClick={onClose}>Close</button>
            <button className="sdp-btn sdp-btn--primary" onClick={() => setEditing(true)}>Edit pay</button>
          </>
        )}
      </div>
    </SlidePanel>
  );
}

export default StaffPayDetail;
